import React, { useState } from 'react';

import { Add } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { assetKey } from './types';
import type { RoomAssetLink } from './types';

type LinkRoomToAssetDialogProps = {
  assets: Array<RoomAssetLink>;
  onAddRoom: (asset: RoomAssetLink) => void;
  onClose: () => void;
  onCreateAsset: (asset: RoomAssetLink) => void;
  open: boolean;
  takenAssetKeys: Set<string>;
};

export default function LinkRoomToAssetDialog({
  assets,
  onAddRoom,
  onClose,
  onCreateAsset,
  open,
  takenAssetKeys,
}: LinkRoomToAssetDialogProps) {
  const [selectedKey, setSelectedKey] = useState('');
  const [mode, setMode] = useState<'link' | 'create'>('link');
  const [newAssetName, setNewAssetName] = useState('');

  function handleAddRoom() {
    const asset = assets.find((candidate) => assetKey(candidate) === selectedKey);
    if (asset) {
      onAddRoom(asset);
    }
    onClose();
  }

  function handleCreateAsset() {
    const asset = { name: newAssetName.trim() };
    onCreateAsset(asset);
    onAddRoom(asset);
    onClose();
  }

  function resetState() {
    setSelectedKey('');
    setMode('link');
    setNewAssetName('');
  }

  if (mode === 'create') {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={onClose}
        open={open}
        slotProps={{ transition: { onExited: resetState } }}
      >
        <DialogTitle>Create new asset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            onChange={(e) => setNewAssetName(e.target.value)}
            sx={{ mt: 1 }}
            value={newAssetName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMode('link')} variant="text">
            Back
          </Button>
          <Button disabled={!newAssetName.trim()} onClick={handleCreateAsset} variant="contained">
            Create and add room
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open} slotProps={{ transition: { onExited: resetState } }}>
      <DialogTitle>Link room to asset</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2">
          Select an existing asset, or create a new one.
        </Typography>

        {assets.length === 0 ? (
          <Typography color="text.secondary" mt={2} variant="body2">
            No assets yet — create one below.
          </Typography>
        ) : (
          <RadioGroup
            onChange={(e) => setSelectedKey(e.target.value)}
            sx={{ flexWrap: 'nowrap', gap: 1, maxHeight: 320, mt: 2, overflowY: 'auto' }}
            value={selectedKey}
          >
            {assets.map((asset) => {
              const taken = takenAssetKeys.has(assetKey(asset));
              return (
                <FormControlLabel
                  control={<Radio />}
                  disabled={taken}
                  key={assetKey(asset)}
                  label={asset.name}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, m: 0, px: 1.5, py: 1 }}
                  value={assetKey(asset)}
                />
              );
            })}
          </RadioGroup>
        )}

        <Button fullWidth onClick={() => setMode('create')} startIcon={<Add />} sx={{ mt: 1.5 }} variant="outlined">
          Create new asset
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button disabled={!selectedKey} onClick={handleAddRoom} variant="contained">
          Add room
        </Button>
      </DialogActions>
    </Dialog>
  );
}
